import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../../components/admin/AdminLayout';
import PaymentsTabs from '../../../components/admin/PaymentsTabs';
import RafikiSettingsCard from '../../../components/admin/RafikiSettingsCard';
import axios from 'axios';
import {
  Settings as SettingsIcon,
  Loader2,
  Copy,
  CheckCircle2,
  AlertCircle,
  Link2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CALLBACK_PATH = '/api/payments/webhook/pawapay';

const STATUS_LABEL = {
  not_configured: { text: 'Not Configured', color: 'bg-slate-500/20 text-slate-400 border-slate-500/30' },
  token_pending: { text: 'Token Pending', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  ready: { text: 'Ready', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
};

const PaymentsSettings = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [copied, setCopied] = useState(false);

  // form fields
  const [environment, setEnvironment] = useState('sandbox');
  const [country, setCountry] = useState('');
  const [currency, setCurrency] = useState('');
  const [publicKey, setPublicKey] = useState('');
  const [token, setToken] = useState('');
  const [testing, setTesting] = useState(false);
  const [connResult, setConnResult] = useState(null);

  const callbackUrl = `${API_URL}${CALLBACK_PATH}`;

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/payments/config`);
      const c = res.data;
      setConfig(c);
      setEnvironment(c.environment || 'sandbox');
      setCountry(c.default_country || '');
      setCurrency(c.default_currency || '');
    } catch (e) {
      console.error('Error fetching payment config:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const handleSave = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(''); setSaving(true);
    try {
      const payload = {
        environment,
        default_country: country,
        default_currency: currency,
      };
      if (publicKey.trim()) payload.callback_public_key = publicKey.trim();
      if (token.trim()) payload.api_token = token.trim();
      const res = await axios.put(`${API_URL}/api/admin/payments/config`, payload);
      setConfig(res.data);
      setPublicKey('');
      setToken('');
      setSuccess('PawaPay configuration saved.');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const copyCallback = () => {
    navigator.clipboard.writeText(callbackUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const testConnection = async () => {
    setTesting(true);
    setConnResult(null);
    try {
      const res = await axios.get(`${API_URL}/api/admin/payments/connection`);
      setConnResult(res.data);
    } catch (e) {
      setConnResult({ status: 'api_error', message: e.response?.data?.detail || 'Request failed' });
    } finally {
      setTesting(false);
    }
  };

  const CONN_STATUS = {
    connected: { text: 'Connected', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
    invalid_token: { text: 'Invalid Token', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
    not_configured: { text: 'Not Configured', color: 'bg-slate-500/20 text-slate-400 border-slate-500/30' },
    api_error: { text: 'API Error', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
    network_error: { text: 'Network Error', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
    timeout: { text: 'Timeout', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  };

  const status = STATUS_LABEL[config?.status] || STATUS_LABEL.not_configured;

  return (
    <AdminLayout>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white mb-2 flex items-center gap-2" data-testid="payments-settings-title">
          <SettingsIcon className="w-6 h-6 text-amber-400" />
          Payments
        </h1>
        <p className="text-slate-400">Configure the PawaPay provider and register your callback URL.</p>
      </div>

      <PaymentsTabs />

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-amber-500" /></div>
      ) : (
        <div className="max-w-2xl space-y-6">
          {error && (
            <div data-testid="settings-error" className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400">
              <AlertCircle className="w-5 h-5" /><span className="text-sm">{error}</span>
            </div>
          )}
          {success && (
            <div data-testid="settings-success" className="flex items-center gap-3 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg text-emerald-400">
              <CheckCircle2 className="w-5 h-5" /><span className="text-sm">{success}</span>
            </div>
          )}

          {/* Callback URL — the key deliverable for PawaPay dashboard registration */}
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-2">
              <Link2 className="w-5 h-5 text-amber-400" />
              <h2 className="text-lg font-semibold text-white">Callback (Webhook) URL</h2>
            </div>
            <p className="text-sm text-slate-400 mb-3">
              Register this URL in your PawaPay dashboard so PawaPay can deliver payment status callbacks.
            </p>
            <div className="flex items-center gap-2">
              <code data-testid="callback-url" className="flex-1 px-3 py-2.5 bg-slate-900/70 border border-slate-700 rounded-lg text-amber-300 text-sm break-all font-mono">
                {callbackUrl}
              </code>
              <button
                onClick={copyCallback}
                data-testid="copy-callback-url"
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-amber-500 text-slate-900 font-semibold rounded-lg hover:bg-amber-400 transition-colors text-sm whitespace-nowrap"
              >
                {copied ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
          </div>

          {/* Provider status + config form */}
          <form onSubmit={handleSave} className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6 space-y-5">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">PawaPay Configuration</h2>
              <span data-testid="provider-status-badge" className={`px-3 py-1 text-xs font-medium rounded border ${status.color}`}>
                {status.text}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Environment</label>
                <select
                  data-testid="env-select"
                  value={environment}
                  onChange={(e) => setEnvironment(e.target.value)}
                  className="w-full px-3 py-2.5 bg-slate-900/50 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500"
                >
                  <option value="sandbox">Sandbox</option>
                  <option value="production">Production</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Default Country <span className="text-slate-500 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  data-testid="country-input"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  placeholder="e.g. RWA"
                  className="w-full px-3 py-2.5 bg-slate-900/50 border border-slate-700 rounded-lg text-white text-sm uppercase focus:outline-none focus:border-amber-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Default Currency <span className="text-slate-500 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  data-testid="currency-input"
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  placeholder="e.g. RWF"
                  className="w-full px-3 py-2.5 bg-slate-900/50 border border-slate-700 rounded-lg text-white text-sm uppercase focus:outline-none focus:border-amber-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Callback Public Key <span className="text-slate-500 font-normal">(optional)</span>
              </label>
              <textarea
                data-testid="public-key-input"
                value={publicKey}
                onChange={(e) => setPublicKey(e.target.value)}
                placeholder={config?.callback_public_key_set ? 'A key is already saved. Paste a new key to replace it.' : 'Paste PawaPay callback public key'}
                rows={3}
                className="w-full px-3 py-2.5 bg-slate-900/50 border border-slate-700 rounded-lg text-white text-sm font-mono focus:outline-none focus:border-amber-500"
              />
              <p className="text-xs text-slate-500 mt-1">
                Required only when Signed Callbacks are enabled inside PawaPay.
              </p>
            </div>

            {/* API Token (secure) */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                PawaPay API Token
                <span
                  data-testid="token-status-badge"
                  className={`ml-2 px-2 py-0.5 text-xs font-medium rounded border ${
                    config?.token_set
                      ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
                      : 'bg-slate-500/20 text-slate-400 border-slate-500/30'
                  }`}
                >
                  {config?.token_set ? 'Token configured' : 'Not configured'}
                </span>
              </label>
              <input
                type="password"
                data-testid="api-token-input"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                autoComplete="off"
                placeholder={config?.token_set ? '•••••••• saved — paste a new token to replace' : 'Paste your PawaPay API token'}
                className="w-full px-3 py-2.5 bg-slate-900/50 border border-slate-700 rounded-lg text-white text-sm font-mono focus:outline-none focus:border-amber-500"
              />
              <p className="text-xs text-slate-500 mt-1">
                The token is encrypted at rest and never displayed again. Use the Sandbox or Production token matching the selected environment.
              </p>
            </div>

            <button
              type="submit"
              disabled={saving}
              data-testid="save-config-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-500 to-amber-600 text-slate-900 font-semibold rounded-lg hover:from-amber-400 hover:to-amber-500 transition-all disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Save Configuration
            </button>
          </form>

          {/* Connection test */}
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-white">Connection Test</h2>
              {connResult && (
                <span
                  data-testid="conn-status-badge"
                  className={`px-3 py-1 text-xs font-medium rounded border ${(CONN_STATUS[connResult.status] || CONN_STATUS.api_error).color}`}
                >
                  {(CONN_STATUS[connResult.status] || CONN_STATUS.api_error).text}
                </span>
              )}
            </div>
            <p className="text-sm text-slate-400 mb-3">
              Verifies the stored token against PawaPay <code className="text-amber-300">/v2/active-conf</code>.
            </p>
            {connResult && (
              <p className="text-sm text-slate-300 mb-3" data-testid="conn-message">
                {connResult.message}
                {connResult.status === 'connected' && connResult.country_codes?.length > 0 && (
                  <> — {connResult.country_codes.length} countries, {connResult.currencies?.join(', ')}</>
                )}
              </p>
            )}
            <button
              onClick={testConnection}
              disabled={testing}
              data-testid="test-connection-btn"
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-700/50 text-slate-200 rounded-lg hover:bg-slate-700 transition-colors text-sm disabled:opacity-50"
            >
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
              Test Connection
            </button>
          </div>

          {/* Rafiki Sandbox — separate provider, same secure config pattern */}
          <RafikiSettingsCard />
        </div>
      )}
    </AdminLayout>
  );
};

export default PaymentsSettings;
